import { ColDef, ICellRendererParams } from "ag-grid-community";
import { Box } from "@mui/material";
import { Button } from "@platform/design-system";
import { useMemo, useState } from "react";

import { CARD_SX } from "./constants";
import { fmtCurrencyExact } from "./formatters";
import { CardTitle, TierChip } from "./primitives";
import { StewardshipTable } from "./StewardshipTable";
import { compareCarrierTier } from "./tierComparator";

import type { CarrierTier } from "../types";

type CarrierPerformanceDatum = {
  key: string;
  name: string;
  tier: CarrierTier;
  marketedWonPremium: number;
  retainedPremium: number;
  rfpCount: number;
};

type CarrierPerformanceCardProps = {
  carriers: CarrierPerformanceDatum[];
};

const PAGE_SIZE = 8;

const currencyFormatter = ({ value }: { value: number }): string =>
  fmtCurrencyExact(value);

const TierCell = ({
  value,
}: ICellRendererParams<
  CarrierPerformanceDatum,
  CarrierTier
>): React.JSX.Element | null => (value ? <TierChip tier={value} /> : null);

function CarrierPerformanceCard({
  carriers,
}: CarrierPerformanceCardProps): React.JSX.Element {
  const [showAll, setShowAll] = useState(false);
  // The backend orders carrier_details by incumbent premium, so rank by
  // retained premium here before paging — otherwise the first page can omit
  // the highest-retained carriers it's meant to surface.
  const ranked = useMemo(
    () => [...carriers].sort((a, b) => b.retainedPremium - a.retainedPremium),
    [carriers],
  );
  const displayed = showAll ? ranked : ranked.slice(0, PAGE_SIZE);
  const hasMore = !showAll && ranked.length > PAGE_SIZE;

  const columnDefs = useMemo<ColDef<CarrierPerformanceDatum>[]>(
    () => [
      { field: "name", headerName: "Carrier", flex: 1 },
      {
        field: "tier",
        headerName: "Tier",
        flex: 1,
        cellRenderer: TierCell,
        comparator: compareCarrierTier,
      },
      {
        field: "marketedWonPremium",
        headerName: "New Premium Sold",
        type: "rightAligned",
        valueFormatter: currencyFormatter,
      },
      {
        field: "retainedPremium",
        headerName: "Retained Premium",
        type: "rightAligned",
        valueFormatter: currencyFormatter,
        sort: "desc",
        sortIndex: 0,
      },
      {
        field: "rfpCount",
        headerName: "# RFPs",
        type: "rightAligned",
      },
    ],
    [],
  );

  return (
    <Box data-testid="carrier-performance-card" sx={CARD_SX}>
      <CardTitle>Carrier Performance</CardTitle>
      <Box sx={{ mt: 2 }}>
        <StewardshipTable rowData={displayed} columnDefs={columnDefs} />

        {hasMore && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll(true)}
              sx={{
                color: "text.secondary",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              Show all {carriers.length} carriers
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export { CarrierPerformanceCard };
export type { CarrierPerformanceDatum };
